import { useMemo } from 'react'
import MatterArtifactButton from '@/matter-artifacts/components/MatterArtifactButton'
import { MatterArtifactVersionStatus, type MatterArtifactVersionRead } from '@/matter-artifacts/schemas'
import { RootState } from '@/store/store'
import { useAppSelector } from '@/store/store-hooks'

export function MatterArtifactVersions({
  matterArtifactVersionIds,
  latestMatterArtifactVersionIds,
  canCancelGenerating,
  onCancelGenerating,
  onViewMatterArtifact,
  onViewCurrentMatterArtifact,
}: {
  matterArtifactVersionIds: string[]
  latestMatterArtifactVersionIds: ReadonlySet<string>
  canCancelGenerating: boolean
  onCancelGenerating: () => void
  onViewMatterArtifact: (matterArtifactVersion: MatterArtifactVersionRead) => void
  onViewCurrentMatterArtifact: (matterArtifactVersion: MatterArtifactVersionRead) => void
}) {
  const matterArtifactVersionsById = useAppSelector((state: RootState) => state.matterArtifactsState.versionsById)
  const matterArtifactVersions = useMemo(() => {
    const latestVersionByArtifactId = new Map<string, MatterArtifactVersionRead>()

    for (const matterArtifactVersionId of matterArtifactVersionIds) {
      const matterArtifactVersion = matterArtifactVersionsById[matterArtifactVersionId]
      if (
        !matterArtifactVersion ||
        matterArtifactVersion.status === MatterArtifactVersionStatus.ERROR ||
        matterArtifactVersion.status === MatterArtifactVersionStatus.CANCELLED
      ) {
        continue
      }

      const latestVersion = latestVersionByArtifactId.get(matterArtifactVersion.matter_artifact_id)
      // Durable versions are created_at ordered and live versions are append ordered. Comparing
      // timestamps keeps hydration deterministic; later list position breaks timestamp ties.
      if (!latestVersion || matterArtifactVersion.created_at >= latestVersion.created_at) {
        latestVersionByArtifactId.set(matterArtifactVersion.matter_artifact_id, matterArtifactVersion)
      }
    }

    return [...latestVersionByArtifactId.values()]
  }, [matterArtifactVersionIds, matterArtifactVersionsById])

  return (
    <div className="mt-4 mb-3 space-y-3">
      {matterArtifactVersions.map((matterArtifactVersion) => (
        <MatterArtifactVersion
          key={matterArtifactVersion.matter_artifact_id}
          matterArtifactVersion={matterArtifactVersion}
          isOutdated={!latestMatterArtifactVersionIds.has(matterArtifactVersion.id)}
          canCancelGenerating={canCancelGenerating}
          onCancelGenerating={onCancelGenerating}
          onViewMatterArtifact={onViewMatterArtifact}
          onViewCurrentMatterArtifact={onViewCurrentMatterArtifact}
        />
      ))}
    </div>
  )
}

function MatterArtifactVersion({
  matterArtifactVersion,
  isOutdated,
  canCancelGenerating,
  onCancelGenerating,
  onViewMatterArtifact,
  onViewCurrentMatterArtifact,
}: {
  matterArtifactVersion: MatterArtifactVersionRead
  isOutdated: boolean
  canCancelGenerating: boolean
  onCancelGenerating: () => void
  onViewMatterArtifact: (matterArtifactVersion: MatterArtifactVersionRead) => void
  onViewCurrentMatterArtifact: (matterArtifactVersion: MatterArtifactVersionRead) => void
}) {
  return (
    <div data-matter-artifact-anchor={matterArtifactVersion.matter_artifact_id}>
      <MatterArtifactButton
        matterArtifactVersion={matterArtifactVersion}
        isOutdated={isOutdated}
        canCancelGenerating={canCancelGenerating}
        onCancelGenerating={onCancelGenerating}
        onViewMatterArtifact={onViewMatterArtifact}
        onViewCurrentMatterArtifact={onViewCurrentMatterArtifact}
      />
    </div>
  )
}
